import React from 'react';
import { useDrag } from 'react-dnd';
import CodeCard from '../TextCoder/CodeCard';
import TextCodeModal from '../TextCoder/TextCodeModal';
import TextCodeExcerptsModal from '../TextCoder/TextCodeExcerptsModal';

const ItemType = 'CODE';

const DraggableCode = ({ 
    code, 
    textCodes, 
    setUploadAudioModal, 
    handleUpdateCode, 
    handleAddNewCode, 
    handleDeleteCode, 
    t, 
    isReGroupMode,
    children 
}) => {
    const [{ isDragging }, drag] = useDrag(() => ({
        type: ItemType,
        item: { id: code.uuid, code },
        canDrag: () => {
            if (!isReGroupMode) return false;
            
            // Check if this is a parent code with children
            const hasChildren = textCodes.some(c => c.parent === code.uuid);
            
            // Don't allow dragging parent codes that have children
            if (hasChildren) return false;
            
            return true;
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    }), [isReGroupMode, code, textCodes]);

    const isChild = !!code.parent;
    const hasChildren = textCodes.some(c => c.parent === code.uuid);
    const canDragThisCode = isReGroupMode && (!hasChildren || isChild);

    return (
        <div
            ref={drag}
            className={`relative ${isChild ? 'ml-8' : ''} ${
                isDragging 
                    ? 'opacity-50 cursor-grabbing' 
                    : isReGroupMode 
                        ? canDragThisCode 
                            ? 'cursor-grab' 
                            : 'cursor-not-allowed opacity-60 group'
                        : ''
            }`}
            title={isReGroupMode && hasChildren && !isChild ? t('DetailProjects.Cannot reorganize with children') : ""}
        >
            {/* Tooltip for non-draggable parent codes */}
            {isReGroupMode && hasChildren && !isChild && (
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 whitespace-nowrap">
                    {t('DetailProjects.Cannot reorganize with children')}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                </div>
            )}
            {children || (
                isChild ? (
                    // Child codes: simplified rendering
                    <div
                        className='flex flex-col rounded-md px-3 py-2 bg-opacity-90'
                        style={{ backgroundColor: code.color }}
                        data-testid={`child-code-card-${code.uuid}`}
                    >
                        <span className='font-bold flex items-center gap-2' data-testid={`child-code-card-name-${code.uuid}`}>
                            <div className='bg-black rounded-full text-white text-xs w-6 h-6 flex justify-center items-center' data-testid={`child-code-card-excerpt-count-${code.uuid}`}>
                                {code?.excerpts?.length || 0}
                            </div>
                            {code.name}
                        </span>
                        <span className='text-gray-700 text-sm mt-2' data-testid={`child-code-card-description-${code.uuid}`}>{code.description}</span>
                        {!isReGroupMode && code.reasoning && (
                            <span className='text-gray-600 text-sm mt-2 italic' data-testid={`child-code-card-reasoning-${code.uuid}`}>
                                <strong>Reasoning:</strong> {code.reasoning}
                            </span>
                        )}
                        {!isReGroupMode && (
                            <div className='flex justify-end gap-2 mt-2'>
                                <button className='bg-white text-black rounded-md px-2 py-1 text-xs'
                                    data-testid={`child-code-card-edit-btn-${code.uuid}`}
                                    onClick={() => setUploadAudioModal(
                                        <TextCodeModal
                                            code={code}
                                            handleClose={() => setUploadAudioModal(null)}
                                            handleUpdateCode={handleUpdateCode}
                                            handleAddNewCode={handleAddNewCode}
                                            handleDeleteCode={handleDeleteCode}
                                            textCodes={textCodes}
                                        />
                                    )}>
                                    {t('DetailProjects.Edit')}
                                </button>
                                <button className='bg-black text-white rounded-md px-2 py-1 text-xs'
                                    data-testid={`child-code-card-view-entries-btn-${code.uuid}`}
                                    onClick={() => setUploadAudioModal(
                                        <TextCodeExcerptsModal
                                            handleClose={() => setUploadAudioModal(null)}
                                            code={code}
                                        />
                                    )}>                        
                                    {t('DetailProjects.View entries')}
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    // Parent codes: use CodeCard
                    <CodeCard
                        code={code}
                        textCodes={textCodes}
                        setUploadAudioModal={setUploadAudioModal}
                        handleUpdateCode={handleUpdateCode}
                        handleAddNewCode={handleAddNewCode}
                        handleDeleteCode={handleDeleteCode}
                        t={t}
                        isReGroupMode={isReGroupMode}
                    />
                )
            )}
        </div>
    );
};

export default DraggableCode;
export { ItemType };